import { Injectable } from '@angular/core';

import { isDefined } from '@frontend/sports/common/core/utils/extended-types';

import { isLinear } from '../../../../base/utils/linear-utils';
import { BetslipType } from '../../../../core/betslip-type';
import { flattenPicks } from '../../../../core/utils';
import { getPickEachWayInfo, isStartingPriceSelected, isXCastPick } from '../../../picks/services/betslip-pick-helpers';
import { ISystemBetPickState } from '../../../system-bet/state';
import { hasCriticalBetslipAndTypeErrorForType, hasCriticalErrorForSlip } from '../../../validation/services/utils/betslip-errors-utils';
import { ICalculatorBetslipInput } from '../../models';
import BaseInputBuilder, { BetslipStateInput, InputBuilderGeneratorOptions } from '../base-input-builder';
import { getSystemSlipId } from '../slip.utils';
import { getSystemBetStake } from './system-bet-input-stake.utils';

// Returns all picks from SystemBet state that are selected and are present in pickList
const defaultSystemPicksSelector = (betslipState: BetslipStateInput) => {
    const isValidPick: (pickState: ISystemBetPickState) => boolean = isLinear(betslipState)
        ? (pickState) => pickState.isSelected && !pickState.isLocked
        : (pickState) => pickState.isSelected;

    return betslipState.picks.pickList
        .map((p): [string, ISystemBetPickState] => [p.id.toString(), betslipState.types.systemBet.picks[p.id.toString()]])
        .filter(([_, p]) => isDefined(p))
        .filter(([_, p]) => isValidPick(p));
};
@Injectable()
export default class SystemBetInputBuilder extends BaseInputBuilder {
    *getBetGeneratorInput(
        rawBetslipState: Omit<BetslipStateInput, 'rewardTokensState'>,
        system = rawBetslipState.types.systemBet.systemInfo,
        options?: InputBuilderGeneratorOptions<ISystemBetPickState>,
    ): Iterable<ICalculatorBetslipInput> {
        if (!system) {
            return;
        }
        const betslipState = { ...rawBetslipState, picks: { ...rawBetslipState.picks, pickList: flattenPicks(rawBetslipState.picks.pickList) } };

        const picksSelector = options?.picksSelector ?? defaultSystemPicksSelector;
        const systemBetPicks = picksSelector(betslipState);
        if (systemBetPicks.length < 3) {
            return;
        }

        const stake = getSystemBetStake(betslipState, system);

        const slipId = getSystemSlipId(system);
        yield {
            slipId,
            items: systemBetPicks.map(([pickId, systemPick]) => {
                const pick = betslipState.picks.pickList.find((p) => p.id.isEqual(pickId))!;

                const isEachWay = isLinear(betslipState)
                    ? betslipState.types.systemBet.linearTypeStakes[system.key].isEachWay
                    : betslipState.types.systemBet.isEachWay;

                return {
                    ...this.getPickInfo(pick),
                    eachWayInfo: isEachWay ? getPickEachWayInfo(pick) : null,
                    isBanker: systemPick.isBanker,
                    isXCast: isXCastPick(pick),
                    isStartingPrice: isStartingPriceSelected(pick),
                };
            }),
            rewardToken: null,
            stake,
            hasError:
                hasCriticalBetslipAndTypeErrorForType(betslipState.errors, BetslipType.System) ||
                hasCriticalErrorForSlip(betslipState.errors, slipId),
            system,
        };
    }
}
